import { Box, Button, CardContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { ClaimPurchaseForm } from '@/components/billing/ClaimPurchaseForm';
import { getPaywallCopy } from '@/components/billing/paywallCopy';
import { PlanPicker } from '@/components/billing/PlanPicker';
import { AuthBrandHeader } from '@/components/common/AuthBrandHeader';
import { FixedCard } from '@/components/common/FixedCard';
import { useOpenHomeHQBillingPortal } from '@/hooks/useHomeHQAccess';
import { useHousehold } from '@/hooks/useHousehold';
import { HomeHQAccess } from '@/models/Subscription';

/** Centered card in the login card's style (logo, 400px, full-width on mobile). */
export const PaywallCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: { xs: 'auto', md: 'calc(100vh - 64px)' },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 0, md: 2 },
      py: { xs: 2, md: 4 },
    }}>
    <FixedCard sx={{ minHeight: { md: 'auto' } }}>
      <CardContent sx={{ gap: 4, alignItems: 'center', pt: { xs: 6, md: 8 }, pb: 5 }}>
        <AuthBrandHeader />
        {children}
      </CardContent>
    </FixedCard>
  </Box>
);

/** Shown in place of Home HQ when the caller (or their household) has no access. */
export const HomeHQPaywall = ({ access }: { access: HomeHQAccess }) => {
  const { data: household } = useHousehold(!!access.householdOwnerId);
  const portal = useOpenHomeHQBillingPortal();
  const copy = getPaywallCopy(access, household);
  const [isClaiming, setIsClaiming] = useState(false);

  if (isClaiming) {
    return (
      <PaywallCard>
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, textAlign: 'center' }}>
          Link your HomeHQ purchase
        </Typography>
        <Box sx={{ width: '100%' }}>
          <ClaimPurchaseForm onCancel={() => setIsClaiming(false)} />
        </Box>
      </PaywallCard>
    );
  }

  return (
    <PaywallCard>
      <Stack spacing={1.5} sx={{ textAlign: 'center', width: '100%' }} data-testid="homehq-paywall">
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, textWrap: 'balance' }}>
          {copy.title}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 14, textWrap: 'balance' }}>
          {copy.body}
        </Typography>
      </Stack>

      <Box sx={{ width: '100%' }}>
        {copy.action === 'choose_plan' && <PlanPicker ctaLabel={copy.cta} />}

        {copy.action === 'renew' && (
          <Stack spacing={1}>
            <Button
              fullWidth
              sx={{ fontSize: 14 }}
              disabled={portal.isPending || !access.canManageBilling}
              onClick={() => portal.mutate()}>
              {portal.isPending ? 'Opening…' : copy.cta}
            </Button>
            {portal.isError && (
              <Typography role="alert" variant="caption" color="error" textAlign="center">
                Could not open billing. Please try again.
              </Typography>
            )}
          </Stack>
        )}

        {copy.action === 'contact_owner' && copy.owner?.email && (
          <Button
            fullWidth
            sx={{ fontSize: 14 }}
            href={`mailto:${copy.owner.email}?subject=${encodeURIComponent(
              'Renew our Hauser subscription',
            )}`}>
            {copy.cta}
          </Button>
        )}

        {copy.action !== 'contact_owner' && (
          <Button
            variant="text"
            fullWidth
            sx={{ mt: 1, fontSize: 13 }}
            onClick={() => setIsClaiming(true)}>
            Already purchased HomeHQ? Link your purchase
          </Button>
        )}
      </Box>
    </PaywallCard>
  );
};
